// import { useState } from 'react';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import WebGL from 'three/addons/capabilities/WebGL.js';


import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

// Object loading functions from other file
import { loadObject } from '../src/ThreeFunctions.tsx';
// import { loadObjectWithAnimation } from '../src/ThreeFunctions.tsx';
// import { createSnowEffect, type SnowSystem } from './SnowGfx.tsx';

// post processing
import { BlendFunction, BloomEffect, EffectComposer, EffectPass, RenderPass } from "postprocessing";
import { NoiseEffect } from 'postprocessing';
import { AnimationMixer, Clock } from "three";

// Responsive design lib
import { useMediaQuery } from "react-responsive";

// import { DecalGeometry } from 'three/examples/jsm/Addons.js';

interface Props {
    pageState: string;
    setPage: Function;
    setEndLoadingScreen: Function;
}

import * as TWEEN from "three/addons/libs/tween.module.js";
import Projects from './Projects.tsx';
import { loadTrafficLane, animateTrafficLane, NUM_CARS_PER_LANE, type TrafficLaneRefs } from './TrafficGfx.tsx';


function ThreeScene({ pageState, setEndLoadingScreen }: Props) {
    // Hook to check if on mobile
    const isMobile: Boolean = useMediaQuery({ maxWidth: 767 });

    const projects = [
        "models/display/cleansweep.png",
        "models/display/riot.png",
        "models/display/raycast.png",
        "models/display/quantum.png",
        "models/display/roblox.png",
        "models/display/poster0.png",
        "models/display/curve.png",];

    const [projCount, setProjCount] = useState<number>(0);
    const [projList] = useState<string[]>(projects); // first poster
    // refs to keep track of same references through component's life
    const homeModelRef = useRef<THREE.Object3D | null>(null);
    const [homeModel, setHomeModel] = useState<THREE.Object3D | null>(null);
    const projModelRef = useRef<THREE.Object3D | null>(null);


    const sceneRef = useRef<THREE.Scene>();
    const cameraRef = useRef<THREE.PerspectiveCamera>();
    const rendererRef = useRef<THREE.WebGLRenderer>();
    const controlsRef = useRef<OrbitControls>();

    // variables for the particle object
    const clockRef = useRef<Clock>(new Clock()); // for animations
    const animMixerRef = useRef<AnimationMixer>(); // for now, for particle animation
    // const particlesRef = useRef<THREE.Object3D>();

    // refs for decal
    const decalPlaneRef = useRef<THREE.Mesh>();
    const billboardCubeRef = useRef<THREE.Mesh>();
    const texturesListRef = useRef<THREE.Texture[]>([]);

    // ref for snow system
    // const snowSystemRef = useRef<SnowSystem | null>(null);

    // refs for traffic curves
    const eastForwardLane: TrafficLaneRefs = {
        instancedMesh: useRef<THREE.InstancedMesh | null>(null),
        curve: useRef<THREE.Curve<THREE.Vector3> | null>(null),
        progressOffsets: useRef<number[]>([])
    };

    const eastOncomingLane: TrafficLaneRefs = {
        instancedMesh: useRef<THREE.InstancedMesh | null>(null),
        curve: useRef<THREE.Curve<THREE.Vector3> | null>(null),
        progressOffsets: useRef<number[]>([])
    };

    const westForwardLane: TrafficLaneRefs = {
        instancedMesh: useRef<THREE.InstancedMesh | null>(null),
        curve: useRef<THREE.Curve<THREE.Vector3> | null>(null),
        progressOffsets: useRef<number[]>([])
    };

    const westOncomingLane: TrafficLaneRefs = {
        instancedMesh: useRef<THREE.InstancedMesh | null>(null),
        curve: useRef<THREE.Curve<THREE.Vector3> | null>(null),
        progressOffsets: useRef<number[]>([])
    };

    // for post processing
    let composer = useRef<EffectComposer>(new EffectComposer(rendererRef.current)).current;

    // Dict for max orbital control distances for each page
    let pageStateMaxDistances: {
        home: number,
        stuff: number,
        notes: number,
        about: number
    } = {
        home: 80,
        stuff: 0,
        notes: 30,
        about: 30
    };

    // camera positions for Desktop
    let pageStateCamPositions: {
        home: THREE.Vector3,
        stuff: THREE.Vector3,
        notes: THREE.Vector3
        about: THREE.Vector3,
    } = {
        home: new THREE.Vector3(0, 0, 0),
        stuff: new THREE.Vector3(-37, 6, 18),
        notes: new THREE.Vector3(30, 10, 0),
        about: new THREE.Vector3(-10, 10, -50)

    }

    // camera positions for Mobile
    let pageStateCamPositionsMobile: {
        home: THREE.Vector3,
        stuff: THREE.Vector3,
        notes: THREE.Vector3,
        about: THREE.Vector3,
    } = {
        home: new THREE.Vector3(0, 0, 0), // unchanged
        stuff: new THREE.Vector3(-37, 6, 18.5),
        notes: new THREE.Vector3(20, 10, 0), // unchanged
        about: new THREE.Vector3(-10, 10, -50) // unchanged

    }


    // set up scene, camera, and renderer
    useEffect(() => {

        sceneRef.current = new THREE.Scene();
        cameraRef.current = new THREE.PerspectiveCamera(55, window.innerWidth /
            window.innerHeight, 0.1, 2000);


        // turn on antialiasing: {antialias: true} inside WebGLRenderer()
        rendererRef.current = new THREE.WebGLRenderer({ powerPreference: "high-performance", antialias: false });
        rendererRef.current.setSize(window.innerWidth, window.innerHeight);
        rendererRef.current.setPixelRatio(window.devicePixelRatio);


        addEventListener("resize", () => { rendererRef.current!.setSize(window.innerWidth, window.innerHeight); });




        // adding controls for camera
        controlsRef.current = new OrbitControls(cameraRef.current, rendererRef.current.domElement);
        cameraRef.current.position.x = -20;
        cameraRef.current.position.y = 20;
        cameraRef.current.position.z = 65;
        cameraRef.current.updateProjectionMatrix();

        controlsRef.current.maxPolarAngle = Math.PI / 2; // prevent camera past ground level
        controlsRef.current.enableDamping = true;
        controlsRef.current.minDistance = 1.7;
        controlsRef.current.target.set(50, -50, 0);
        controlsRef.current.panSpeed = 0.8;
        controlsRef.current.maxDistance = 2000;
        controlsRef.current.autoRotate = true;
        controlsRef.current.autoRotateSpeed = 0.02;


        // Add grid helper
        // const gridHelper: THREE.GridHelper = new THREE.GridHelper(3000, 1000);
        // sceneRef.current?.add(gridHelper);

        // console.log(projList);

        // cleanup
        return () => {
            window.removeEventListener("resize", () => { rendererRef.current!.setSize(window.innerWidth, window.innerHeight); });
        }

    }, []);
    // set up test cube
    // const geometry = new THREE.BoxGeometry( 1, 1, 1);
    // const material = new THREE.MeshBasicMaterial( {color: 0x00ff00 });
    // const cube = new THREE.Mesh(geometry, material);
    // if (sceneRef.current)
    // {
    //     cube.position.x = -8;
    //     cube.position.y = 0;
    //     cube.position.z = 2;

    //     sceneRef.current.add( cube );
    // }


    useEffect(() => {
        // load textures
        let decalTexture: THREE.Texture;
        let decalPath: string;
        for (let i = 0; i < projList.length; i++) {
            decalPath = projList[i];
            decalTexture = new THREE.TextureLoader().load(decalPath);
            texturesListRef.current.push(decalTexture);
        }
        console.log("Finished loading project images.");

        // cleanup
        return () => {
            texturesListRef.current.forEach(texture => texture.dispose());
            texturesListRef.current = [];
        }
    }, []);
    useEffect(() => {
        console.log("Loading display object");
        let isMounted = true;
        async function loadDisplay() {
            try {
                // const loadedModel = await loadObject('models/display/floor.gltf', sceneRef.current!);

                if (isMounted) {
                    // sceneRef.current?.add(loadedModel);
                    // loadedModel.position.set(-8, 0, 2);
                    // projModelRef.current = loadedModel;


                    const geometry = new THREE.BoxGeometry(0.05, 1, 1.778);
                    const material = new THREE.MeshStandardMaterial({ color: 0xffffff });
                    const cube = new THREE.Mesh(geometry, material);
                    cube.position.set(-37, 6, 18.5);
                    cube.rotation.y += 0.3;
                    sceneRef.current!.add(cube);
                    billboardCubeRef.current = cube;

                    // const decalTexture = new THREE.TextureLoader().load(decalPath);
                    const decalTexture = texturesListRef.current[projCount];

                    // Create the material for the decal
                    const decalMaterial = new THREE.MeshStandardMaterial({
                        map: decalTexture,
                        transparent: true,
                    });

                    // scanline tv effect on the billboard
                    decalMaterial.onBeforeCompile = (shader) => {
                        shader.vertexShader = 'varying vec2 vCrtUv;\n' + shader.vertexShader.replace(
                            '#include <uv_vertex>', '#include <uv_vertex>\n vCrtUv = uv;');
                        shader.fragmentShader = 'varying vec2 vCrtUv;\n' + shader.fragmentShader.replace(
                            '#include <dithering_fragment>',
                            `#include <dithering_fragment>
                            gl_FragColor.rgb -= sin(vCrtUv.y * 1000.0) * 0.03;
                            gl_FragColor.rgb *= 0.9 + 0.1 * smoothstep(0.9, 0.25, distance(vCrtUv, vec2(0.5)));`);
                    };

                    // define the plane size 
                    const planeGeometry = new THREE.PlaneGeometry(1.778, 1);  // Adjust size as needed

                    // define plane ref
                    decalPlaneRef.current = new THREE.Mesh(planeGeometry, decalMaterial);

                    // sit the screen on cube face
                    decalPlaneRef.current.position.set(-0.026, 0, 0); // just outside the 0.05-thick cube's -x face
                    decalPlaneRef.current.rotation.set(0, -Math.PI / 2, 0);

                    // warm street-lamp glow around the billboard
                    const warmLight = new THREE.PointLight(0xff9a4d, 5, 2, 2);
                    warmLight.position.set(0, 0.5, 1); // local offset, just in front of the screen
                    decalPlaneRef.current.add(warmLight);

                    // parent to the cube so screen + glow follow it
                    cube.add(decalPlaneRef.current);



                }
            } catch (error) {
                console.log("Error loading model");
            }
        }
        loadDisplay();

        // cleanup function
        return () => {
            isMounted = false;
            if (projModelRef.current) {
                console.log("Removing display object");
                sceneRef.current?.remove(projModelRef.current);
            }

            // remove  the whole billboard (cube + screen + glow).
            // NOTE: don't dispose material.map — textures are shared/reused across projects.
            if (billboardCubeRef.current) {
                billboardCubeRef.current.traverse((obj) => {
                    if (obj instanceof THREE.Mesh) {
                        obj.geometry?.dispose();
                        (obj.material as THREE.Material)?.dispose();
                    }
                });
                sceneRef.current?.remove(billboardCubeRef.current);
                billboardCubeRef.current = undefined;
            }
        }




    }, [projCount]);

    // load Toronto object using hook (only once)
    useEffect(() => {
        let isMounted = true;
        async function loadHome() {
            try {
                const loadedModel = await loadObject('models/toronto/toronto.gltf', sceneRef.current!);
                if (isMounted) {
                    sceneRef.current?.add(loadedModel);
                    setHomeModel(loadedModel);
                    homeModelRef.current = loadedModel;
                    setEndLoadingScreen(true);

                }

            } catch (error) {
                console.error("Error loading model (no animation): ", error);
            }
        }
        loadHome();

        return () => {
            isMounted = false;
            if (homeModelRef.current) {
                console.log("removing home");

                // traverse through to dispose of all geometries, materials, and textures
                homeModelRef.current.traverse((child) => {
                    if (child instanceof THREE.Mesh) {
                        if (child.geometry) {
                            child.geometry.dispose();
                        }
                        if (child.material) {
                            if (Array.isArray(child.material)) {
                                child.material.forEach(mat => {
                                    if (mat.map) mat.map.dispose();
                                    if (mat.normalMap) mat.normalMap.dispose();
                                    if (mat.roughnessMap) mat.roughnessMap.dispose();
                                    if (mat.metalnessMap) mat.metalnessMap.dispose();
                                    mat.dispose();
                                });
                            } else {
                                if (child.material.map) child.material.map.dispose();
                                if (child.material.normalMap) child.material.normalMap.dispose();
                                if (child.material.roughnessMap) child.material.roughnessMap.dispose();
                                if (child.material.metalnessMap) child.material.metalnessMap.dispose();
                                child.material.dispose();
                            }
                        }
                    }
                });

                sceneRef.current?.remove(homeModelRef.current);
            }

        }
    }, []); // run once






    // load animated particles using promise (and only once)
    // useEffect(() => {
    //     async function loadModel() {
    //         try {
    //             [particlesRef.current, animMixerRef.current] = await loadObjectWithAnimation('models/particles/scene.gltf', sceneRef.current!);
    //             sceneRef.current?.add(particlesRef.current);
    //             particlesRef.current.scale.set(0.5,0.5,0.5); // NOT OPTIMAL but wtv for now
    //             particlesRef.current.position.set(-37, 6, 18.5);
    //         } catch (error) {
    //             console.error("Error loading particle model with animation:", error);
    //         }
    //     }
    //     // loadModel();

    //     // cleanup
    //     return () => {
    //         // stop animation mixer
    //         if (animMixerRef.current) {
    //             animMixerRef.current.stopAllAction();
    //             animMixerRef.current.uncacheRoot(particlesRef.current!);
    //         }

    //         if (particlesRef.current) {
    //             console.log("Removing particles object");

    //             // dispose
    //             particlesRef.current.traverse((child) => {
    //                 if (child instanceof THREE.Mesh) {
    //                     if (child.geometry) child.geometry.dispose();
    //                     if (child.material) {
    //                         if (Array.isArray(child.material)) {
    //                             child.material.forEach(mat => {
    //                                 if (mat.map) mat.map.dispose();
    //                                 mat.dispose();
    //                             });
    //                         } else {
    //                             if (child.material.map) child.material.map.dispose();
    //                             child.material.dispose();
    //                         }
    //                     }
    //                 }
    //             });

    //             sceneRef.current?.remove(particlesRef.current);
    //         }
    //     };
    // }, []); // only run once on mount

    // load traffic lanes
    useEffect(() => {
        let isMounted = true;

        // oad east forward lane 
        loadTrafficLane(
            'models/Trafficgfx/TrafficCurves/East.glb',
            new THREE.Vector3(0, 0, 0),
            false, // not reversed
            eastForwardLane,
            sceneRef,
            isMounted,
            0x00ff00 // green debug color
        );

        // east oncoming lane (reversed)
        loadTrafficLane(
            'models/Trafficgfx/TrafficCurves/East.glb',
            new THREE.Vector3(0, 0, 0.25), // offset on Z
            true, // reversed
            eastOncomingLane,
            sceneRef,
            isMounted,
            0xff0000 // red debug color
        );

        // west lane
        loadTrafficLane(
            'models/Trafficgfx/TrafficCurves/West.glb',
            new THREE.Vector3(0, 0, 0),
            false, // not reversed
            westForwardLane,
            sceneRef,
            isMounted,
            0x00ff00
        );

        // west oncoming lane
        loadTrafficLane(
            'models/Trafficgfx/TrafficCurves/West.glb',
            new THREE.Vector3(0, 0, 0.25), // offset on Z
            true, // reversed
            westOncomingLane,
            sceneRef,
            isMounted,
            0xff0000
        );

        return () => {
            // cleanup
            isMounted = false;

            // clean up forward lane
            if (eastForwardLane.instancedMesh.current) {
                sceneRef.current?.remove(eastForwardLane.instancedMesh.current);
                eastForwardLane.instancedMesh.current.geometry.dispose();
                if (eastForwardLane.instancedMesh.current.material instanceof THREE.Material) {
                    eastForwardLane.instancedMesh.current.material.dispose();
                }
            }

            // clean up oncoming lane
            if (eastOncomingLane.instancedMesh.current) {
                sceneRef.current?.remove(eastOncomingLane.instancedMesh.current);
                eastOncomingLane.instancedMesh.current.geometry.dispose();
                if (eastOncomingLane.instancedMesh.current.material instanceof THREE.Material) {
                    eastOncomingLane.instancedMesh.current.material.dispose();
                }
            }

            if (westForwardLane.instancedMesh.current) {
                sceneRef.current?.remove(westForwardLane.instancedMesh.current);
                westForwardLane.instancedMesh.current.geometry.dispose();
                if (westForwardLane.instancedMesh.current.material instanceof THREE.Material) {
                    westForwardLane.instancedMesh.current.material.dispose();
                }
            }

            // clean up oncoming lane
            if (westOncomingLane.instancedMesh.current) {
                sceneRef.current?.remove(westOncomingLane.instancedMesh.current);
                westOncomingLane.instancedMesh.current.geometry.dispose();
                if (westOncomingLane.instancedMesh.current.material instanceof THREE.Material) {
                    westOncomingLane.instancedMesh.current.material.dispose();
                }
            }
        };
    }, []);


    // add lighting
    useEffect(() => {


        // const light = new THREE.AmbientLight(0xffffff, 4);
        // const light = new THREE.AmbientLight(0xffffff, 0.01);
        // sceneRef.current?.add(light);

        // display light
        const pl = new THREE.PointLight(0xffffff, 6, 5, 3);
        pl.position.set(-38, 7, 19);
        // const dlHelper = new THREE.PointLightHelper(pl, 1);
        sceneRef.current?.add(pl);


        const plGlobal = new THREE.PointLight(0xffffff, 0.8, 50, 0.2);
        plGlobal.position.set(0, 20, 0);
        sceneRef.current?.add(plGlobal);

    }, []);

    // add stars to the sky
    // useEffect(() => {
    //     const starGeometry = new THREE.BufferGeometry();
    //     const starCount = isMobile ? 2000 : 5000;
    //     const starPositions = new Float32Array(starCount * 3);
    //     const starSizes = new Float32Array(starCount);
    //
    //     for (let i = 0; i < starCount; i++) {
    //         // random positions
    //         const radius = 500 + Math.random() * 1000; // Stars between 500 and 1500 units away
    //         const theta = Math.random() * Math.PI * 2; // Random angle around Y axis
    //         const phi = Math.random() * Math.PI / 2;
    //
    //         starPositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
    //         starPositions[i * 3 + 1] = radius * Math.cos(phi);
    //         starPositions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta);
    //
    //         // rand star sizes
    //         starSizes[i] = Math.random() * 3 + 1;
    //     }
    //
    //     starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    //     starGeometry.setAttribute('size', new THREE.BufferAttribute(starSizes, 1));
    //
    //     // star materials
    //     const starMaterial = new THREE.PointsMaterial({
    //         color: 0xffffff,
    //         size: 2,
    //         sizeAttenuation: true,
    //         transparent: true,
    //         opacity: 0.8,
    //         blending: THREE.AdditiveBlending,
    //     });
    //
    //     const stars = new THREE.Points(starGeometry, starMaterial);
    //     sceneRef.current?.add(stars);
    //
    //     // Cleanup
    //     return () => {
    //         sceneRef.current?.remove(stars);
    //         starGeometry.dispose();
    //         starMaterial.dispose();
    //     };
    // }, []);
    //
    // add snow effect
    // useEffect(() => {
    //     // create snow system
    //     const snowSystem = createSnowEffect(Boolean(isMobile));
    //     snowSystemRef.current = snowSystem;
    //     sceneRef.current?.add(snowSystem.particles);
    //
    //     // cleanup
    //     return () => {
    //         if (snowSystemRef.current) {
    //             sceneRef.current?.remove(snowSystemRef.current.particles);
    //             snowSystemRef.current.dispose(); // clear from gpu memory
    //             snowSystemRef.current = null;
    //         }
    //     };
    // }, [isMobile]);
    //
    //
    // add post processing effects
    useEffect(() => {
        // post processing effects
        composer = new EffectComposer(rendererRef.current);
        composer.addPass(new RenderPass(sceneRef.current, cameraRef.current));
        composer.addPass(new EffectPass(cameraRef.current, new BloomEffect({ intensity: 1, luminanceThreshold: .5, radius: 0.5 })));
        const noiseEffect = new NoiseEffect({
            blendFunction: BlendFunction.SCREEN,
            premultiply: true,
        });

        noiseEffect.blendMode.opacity.value = 0.90; // control strength of effect
        composer.addPass(new EffectPass(cameraRef.current, noiseEffect));

        return () => {
            // Dispose composer and all its passes
            composer.dispose();
        };
    }, []);




    // Render loops
    useEffect(() => {
        requestAnimationFrame(function render() {

            requestAnimationFrame(render);
            composer.render();

            // if (homeModel) {
            // homeModel.rotation.y += 0.0002;
            // }
            // controlsRef.current!.autoRotate = true;
            // controlsRef.current!.autoRotateSpeed = 0.05;
            controlsRef.current?.update(); // camera controls update
        });

        // animation loop
        function animate() {
            const deltaTime = clockRef.current.getDelta();
            if (animMixerRef.current) {
                animMixerRef.current.update(deltaTime); // Update animation mixer for particle animations
                animMixerRef.current.timeScale = 0.1;
            }

            // animate all traffic lanes
            animateTrafficLane(eastForwardLane, 0.00012, NUM_CARS_PER_LANE); //ref, speed, num cars
            animateTrafficLane(eastOncomingLane, 0.00006, NUM_CARS_PER_LANE);
            animateTrafficLane(westForwardLane, 0.00006, NUM_CARS_PER_LANE);
            animateTrafficLane(westOncomingLane, 0.00012, NUM_CARS_PER_LANE);

            // update snow particles animation
            // if (snowSystemRef.current) {
            //     snowSystemRef.current.update(deltaTime);
            // }

            TWEEN.update();
        }

        // double check if webGL is compatible (from three js docs)
        if (WebGL.isWebGL2Available()) {
            rendererRef.current!.setAnimationLoop(animate);
        } else {
            const warning = WebGL.getWebGL2ErrorMessage();
            document.getElementById('root')?.appendChild(warning);
        }
    }, [homeModel, cameraRef]);

    // use Ref hook to create a container that renderer.domElement (HTMLCanvasElement) can be added to
    const containerRef = useRef<HTMLDivElement>(null); // create reference to this div element (JSX Element)

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.appendChild(rendererRef.current!.domElement);
        }

        // cleanup when component is deleted
        return () => {
            if (containerRef.current) {
                containerRef.current.removeChild(rendererRef.current!.domElement);
            }
        };

    }, []);


    // wheel over the billboard scrolls the posts
    useEffect(() => {
        const canvas = rendererRef.current?.domElement;
        if (!canvas) return;
        const raycaster = new THREE.Raycaster();
        const ndc = new THREE.Vector2();

        const onWheel = (e: WheelEvent) => {
            const posts = document.getElementById("projects-scroll"); // only exists on the projects page
            if (!posts || !decalPlaneRef.current || !cameraRef.current) return;

            const rect = canvas.getBoundingClientRect();
            ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            raycaster.setFromCamera(ndc, cameraRef.current);

            if (raycaster.intersectObject(decalPlaneRef.current).length > 0) {
                e.preventDefault();
                e.stopImmediatePropagation(); // ignore OrbitControls zoom listener
                posts.scrollBy({ top: e.deltaY });
            }
        };

        canvas.addEventListener("wheel", onWheel, { capture: true, passive: false });
        return () => canvas.removeEventListener("wheel", onWheel, { capture: true });
    }, []);

    // react to pageState changes
    useEffect(() => {
        if (!controlsRef.current) return;
        let targetPosition = new THREE.Vector3();
        const resetPolarAngles = () => {
            controlsRef.current!.maxPolarAngle = Math.PI / 2;
            controlsRef.current!.minPolarAngle = 0;
        };

        // positions for the orbit camera
        switch (pageState) {
            case "home":
                resetPolarAngles();
                controlsRef.current.autoRotate = true;
                controlsRef.current.minDistance = 65;
                // cameraRef.current!.position.set(-20, 20, 65);
                // targetPosition.set(0, 0.5, 0);
                targetPosition = pageStateCamPositions[pageState as keyof typeof pageStateCamPositions];
                break;
            case "stuff":
                resetPolarAngles();
                controlsRef.current.autoRotate = false;
                if (!isMobile) {
                    controlsRef.current.minDistance = 1.7;
                    targetPosition = pageStateCamPositions[pageState as keyof typeof pageStateCamPositions];
                } else {
                    console.log("Mobile");
                    controlsRef.current.minDistance = 4;
                    targetPosition = pageStateCamPositionsMobile[pageState as keyof typeof pageStateCamPositions];

                }
                // cameraRef.current!.position.set(-37, 10, 18.5);
                // cameraRef.current!.lookAt(0, 0, 0);
                break;
            case "notes":
                controlsRef.current.autoRotate = true;
                controlsRef.current.minDistance = 20;
                controlsRef.current.maxPolarAngle = 0; // top down view
                controlsRef.current.minPolarAngle = 0;
                cameraRef.current!.position.set(0, 50, 0); // position above
                targetPosition = pageStateCamPositions[pageState as keyof typeof pageStateCamPositions];
                break;
            case "about":
                resetPolarAngles();
                controlsRef.current.minDistance = 8;
                controlsRef.current.autoRotate = true;
                targetPosition = pageStateCamPositions[pageState as keyof typeof pageStateCamPositions];
                break;
            default:
                resetPolarAngles();
                controlsRef.current.autoRotate = true;
                targetPosition.set(0, 0, 0);
                break;


        }
        // console.log("Smoothly moving camera to ", pageState, targetPosition);

        const startTarget = controlsRef.current.target.clone();
        // console.log('target is!', startTarget)
        const duration = 2000;
        const startTime = performance.now();

        // quadratic ease function for smoothening
        function ease(t: number) {

            // first half (0.5 s) ease in
            // second hald (>0.5 s ) ease out
            return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        }



        function travelTowardsAnimation(newMaxDistance: number) {
            const elapsed = performance.now() - startTime;
            let t = Math.min(elapsed / duration, 1);
            t = ease(t);

            // interpolate camera target position
            controlsRef.current!.target.lerpVectors(startTarget, targetPosition, t);

            // interpolate maxDistance smoothly if it's not the homepage
            // would error since homepage has no maxDistance defined
            if (newMaxDistance != -1) {
                controlsRef.current!.maxDistance += (newMaxDistance - controlsRef.current!.maxDistance) * 0.02;
            }
            controlsRef.current!.update();


            if (t < 1) {
                requestAnimationFrame(() => travelTowardsAnimation(newMaxDistance));
            } else {
                // this asserts that the string pageState is definitely one of the keys in the dict
                controlsRef.current!.maxDistance = pageStateMaxDistances[
                    pageState as keyof typeof pageStateMaxDistances];

                // ending target position (where camera looks at)
                if (pageState === "stuff") {
                    // cameraRef.current!.lookAt(-37, 6, 18.5);
                    if (!isMobile) {
                        controlsRef.current!.target = (pageStateCamPositions[pageState as keyof typeof pageStateCamPositions]);
                    } else {
                        controlsRef.current!.target = targetPosition = pageStateCamPositionsMobile[pageState as keyof typeof pageStateCamPositions];
                    }
                    cameraRef.current!.position.set(-50, 8, 21);
                    controlsRef.current!.update();
                }
            }
        }
        // when home is loaded, maxDistance hasn't been initialized (otherwise it would 
        // cause abrupt movement)
        if (pageState != "home") {
            travelTowardsAnimation(pageStateMaxDistances[
                pageState as keyof typeof pageStateMaxDistances]);
        }
        else {
            travelTowardsAnimation(-1);
        }


    }, [pageState]);




    // component return statement
    return <div className="" ref={containerRef}>
        {
            pageState === "stuff" &&
            <>
                <Projects projCount={projCount} setProjCountFunction={setProjCount} />
            </>
        }
    </div>;
}

// export default React.memo(ThreeScene); // memo makes sure component doesn't re-render if parent re-renders unless props of this comp changes
export default ThreeScene;
